import client from "./client";

export type RiskLevel = "low" | "medium" | "high";
export type InsightSeverity = "info" | "warning" | "success";

export interface FlightRiskRow {
  employee_id: string;
  first_name: string;
  last_name: string;
  title: string;
  department: string | null;
  risk_level: RiskLevel;
  risk_score: number;
  risk_factors: string[];
  recommended_actions: string[];
}

// All payloads now carry cache metadata. ``cached: false`` means the
// response is the result of an explicit POST /run; ``cached: true``
// means it was loaded from the ``cached_analyses`` table. Empty
// state: ``cache_generated_at`` is null/undefined and the data
// arrays are empty — the SPA renders a "No analysis yet, click Run"
// card.
export interface CacheMeta {
  cached?: boolean;
  cache_generated_at?: string | null;
  cache_generated_by?: string | null;
}

export interface FlightRiskReport extends CacheMeta {
  generated_at: string;
  model: string;
  total_analyzed: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  rows: FlightRiskRow[];
  notes: string | null;
}

export interface DepartmentForecast {
  department_id: string | null;
  department_name: string | null;
  employee_count: number;
  historical_turnover_12m: number;
  forecast_90d_pct: number;
  expected_departures: number;
  confidence: "low" | "medium" | "high";
}

export interface TurnoverForecast extends CacheMeta {
  generated_at: string;
  horizon_days: number;
  org_avg_turnover_12m: number;
  org_forecast_90d_pct: number;
  departments: DepartmentForecast[];
}

export interface EngagementDepartment {
  department_id: string | null;
  department_name: string | null;
  employee_count: number;
  avg_engagement_score: number | null;
  response_count: number;
  turnover_rate_12m: number;
  avg_performance_rating: number | null;
  flagged: boolean;
  insight: string | null;
}

export interface EngagementCorrelation extends CacheMeta {
  generated_at: string;
  org_avg_engagement: number | null;
  org_avg_performance: number | null;
  departments: EngagementDepartment[];
}

export interface InsightItem {
  title: string;
  body: string;
  severity: InsightSeverity;
}

export interface WeeklyInsights extends CacheMeta {
  generated_at: string;
  summary: string;
  items: InsightItem[];
}

// ── GET endpoints — return cached results, NEVER call Claude ──

export async function fetchFlightRisk(): Promise<FlightRiskReport> {
  const res = await client.get("/predict/flight-risk");
  return res.data;
}

export async function fetchTurnoverForecast(): Promise<TurnoverForecast> {
  const res = await client.get("/predict/turnover-forecast");
  return res.data;
}

export async function fetchEngagementTrends(): Promise<EngagementCorrelation> {
  const res = await client.get("/predict/engagement-trends");
  return res.data;
}

export async function fetchWeeklyInsights(): Promise<WeeklyInsights> {
  const res = await client.get("/predict/insights");
  return res.data;
}

// ── POST /run endpoints — fresh Claude call, overwrites the cache ──

export async function runFlightRisk(): Promise<FlightRiskReport> {
  const res = await client.post("/predict/flight-risk/run");
  return res.data;
}

export async function runTurnoverForecast(): Promise<TurnoverForecast> {
  const res = await client.post("/predict/turnover-forecast/run");
  return res.data;
}

export async function runEngagementTrends(): Promise<EngagementCorrelation> {
  const res = await client.post("/predict/engagement-trends/run");
  return res.data;
}

export async function runWeeklyInsights(): Promise<WeeklyInsights> {
  const res = await client.post("/predict/insights/run");
  return res.data;
}
